<template>
    <div>
        <div class="score-container flex-col">

            <van-popup v-model:show="showFilterPopup" position="bottom" round class="filter-popup">
                <div class="popup-header">
                    <div class="popup-title">筛选条件</div>
                    <van-button type="primary" size="small" @click="handleFilterConfirm" class="confirm-button">
                        确定
                    </van-button>
                </div>
                <div class="popup-content">
                    <v-merchant-selector @merchant-selected="onMerchantSelected" class="selector-component" />
                    <v-date-range-selector :selected-from="fromDate" :selected-to="toDate"
                        @date-range-changed="onDateRangeChanged" class="selector-component" />
                </div>
            </van-popup>
        </div>

        <div class="fixed-header">
            <van-nav-bar left-text="返回" left-arrow @click-left="backToPreviousPage" title="我的分数"
                class="custom-nav-bar" />
            <div class="filter-section">
                <van-button size="small" type="primary" plain @click="showFilterPopup = true" class="filter-button">
                    <van-icon name="filter" />
                    <span>{{ getFilterSummary() }}</span>
                </van-button>
            </div>
        </div>

        <div class="scrollable flex-1 p-4">
            <div class="stats-grid p-8" v-if="playerScores.length > 0">
                <div class="stat-item compact">
                    <span class="text-sm font-medium">最高分</span>
                    <span class="text-lg font-semibold high-score">{{ maxScore }}</span>
                </div>
                <div class="stat-item compact">
                    <span class="text-sm font-medium">最低分</span>
                    <span class="text-lg font-semibold low-score">{{ minScore }}</span>
                </div>
                <div class="stat-item compact">
                    <span class="text-sm font-medium">平均分</span>
                    <span class="text-lg font-semibold avg-score">{{ avgScore }}</span>
                </div>
                <div class="stat-item compact">
                    <span class="text-sm font-medium">总分</span>
                    <span class="text-lg font-semibold">{{ totalScore }}</span>
                </div>
                <div class="stat-item compact">
                    <span class="text-sm font-medium">局数</span>
                    <span class="text-lg font-semibold">{{ turnCount }}</span>
                </div>
                <div class="stat-item compact">
                    <span class="text-sm font-medium">全中率</span>
                    <span class="text-lg font-semibold">{{ strikePercent }}%</span>
                </div>
                <div class="stat-item compact">
                    <span class="text-sm font-medium">补中率</span>
                    <span class="text-lg font-semibold">{{ sparePercent }}%</span>
                </div>
                <div class="stat-item compact">
                    <span class="text-sm font-medium">失误率</span>
                    <span class="text-lg font-semibold">{{ missPercent }}%</span>
                </div>
                <div class="stat-item compact">
                    <span class="text-sm font-medium">分瓶率</span>
                    <span class="text-lg font-semibold">{{ splitPercent }}%</span>
                </div>
                <div class="stat-item compact">
                    <span class="text-sm font-medium">一球倒瓶</span>
                    <span class="text-lg font-semibold">{{ firstBallKnockdownPercent }}%</span>
                </div>
            </div>
            <div class="card card-hover" v-for="(playerScore, index) in playerScores" :key="index">
                <div class="flex items-center mb-8 px-4">
                    <div class="text-2xl font-bold text-success">{{ playerScore.totalScore }}</div>
                    <div class="flex items-center flex-1">
                        <div class="flex items-center text-gray text-sm mr-4">
                            <van-icon name="location" class="mr-4" />
                            <span>球道: {{ playerScore.laneNumber }}</span>
                        </div>
                        <div class="flex items-center text-gray text-sm">
                            <van-icon name="clock" class="mr-4" />
                            <span>{{ formatDateTime(playerScore.startTime) }}</span>
                        </div>
                    </div>
                </div>

                <div class="score-wrapper">
                    <v-player-score class="player-score-component" @round-click="onClickPlayerRoundScore"
                        :show-name="showName" :player-index="index" :player-score="playerScore" />
                </div>
            </div>

            <div class="empty-state flex-center" v-if="playerScores.length === 0">
                <van-empty description="暂无分数记录" image="search" />
            </div>
        </div>

        <van-popup v-model:show="showBigPinObj.visible" position="center" round closeable class="pin-popup">
            <div class="p-16">
                <div class="text-xl font-semibold text-center mb-16">击球详情</div>
                <div class="flex flex-wrap justify-center gap-4">
                    <v-pin-state radius="27.5" w="8rem" h="8rem" :enableSetPin="false" :pins1="showBigPinObj.pins1"
                        class="pin-state" />
                    <v-pin-state radius="27.5" w="8rem" h="8rem" :enableSetPin="false" :pins1="showBigPinObj.pins2"
                        class="pin-state" />
                    <v-pin-state radius="27.5" w="8rem" h="8rem" :enableSetPin="false" :pins1="showBigPinObj.pins3"
                        class="pin-state" />
                </div>
            </div>
        </van-popup>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import vPlayerScore from "@/components/bowling/PlayerScore.vue";
import vMerchantSelector from "@/components/user/MerchantSelector.vue";
import vDateRangeSelector from "@/components/user/DateRangeSelector.vue";
import apiUtil from "@/global/apiUtil";
import util from "@/global/util";
import vPinState from "@/components/bowling/PinState.vue";
import { defineProps, defineEmits } from 'vue';
import { useRouter } from 'vue-router';
import scoreUtil from '@/global/scoreUtil';
import { Icon } from 'vant';
import { Empty as VanEmpty } from 'vant';

const props = defineProps({});
const emit = defineEmits(['round-click']);
const router = useRouter();

const showBigPinObj = ref({
    visible: false,
    pins1: [],
    pins2: [],
    pins3: []
});
const showFilterPopup = ref(false);
const userId = ref('');
const playerScores = ref([]);
const maxScore = ref('');
const minScore = ref('');
const avgScore = ref('');
const selectedMerchant = ref({});
const showName = ref(false);
const fromDate = ref(util.formatDate(new Date()));
const toDate = ref(util.formatDate(new Date()));

const formatDateTime = (datetime) => {
    if (!datetime) return '';
    return util.formatDateTime(datetime);
};

const getFilterSummary = () => {
    const merchant = selectedMerchant.value.name || '全部场馆';
    const from = util.formatDate(fromDate.value);
    const to = util.formatDate(toDate.value);
    return `${merchant} | ${from} 至 ${to}`;
};

onMounted(async () => {
    const fd = new Date();
    fd.setDate(fd.getDate() - 7);
    fromDate.value = util.formatDate(fd);

    const td = new Date();
    toDate.value = util.formatDate(td);

    userId.value = localStorage.getItem("userId");
    await getPlayerScores();
});

const onMerchantSelected = (merchant) => {
    selectedMerchant.value = merchant;
};

const onDateRangeChanged = (f, t) => {
    fromDate.value = util.formatDate(f.value);
    toDate.value = util.formatDate(t.value);
};

const handleFilterConfirm = async () => {
    showFilterPopup.value = false;
    await getPlayerScores();
};

const totalScore = ref(0);
const turnCount = ref(0);
const strikePercent = ref(0);
const sparePercent = ref(0);
const missPercent = ref(0);
const splitPercent = ref(0);
const firstBallKnockdownPercent = ref(0);

const calculateStats = (scores) => {
    if (!scores?.statistics) return;

    const stats = scores.statistics;
    const totalFrames = stats.roundCount || 0;

    totalScore.value = stats.total || 0;
    turnCount.value = stats.turnCount || 0;
    const secondBallCount = (stats.missCount || 0) + (stats.spareCount || 0);
    strikePercent.value = totalFrames > 0 ? (((stats.strikeCount || 0) / totalFrames) * 100).toFixed(1) : 0;
    sparePercent.value = totalFrames > 0 ? (((stats.spareCount || 0) / secondBallCount) * 100).toFixed(1) : 0;
    missPercent.value = totalFrames > 0 ? (((stats.missCount || 0) / secondBallCount) * 100).toFixed(1) : 0;
    splitPercent.value = totalFrames > 0 ? (((stats.splitCount || 0) / totalFrames) * 100).toFixed(1) : 0;
    firstBallKnockdownPercent.value = totalFrames > 0 ? (((stats.firstKickPinCount || 0) / (totalFrames * 10)) * 100).toFixed(1) : 0;
};

const getPlayerScores = async () => {
    if (!fromDate.value || !toDate.value) return;
    if (selectedMerchant.value.id == null) return;

    const queryObj = {
        From: fromDate.value,
        To: toDate.value,
        merchantId: selectedMerchant.value.id,
    };

    const result = await apiUtil.apiPost("Bowler/MyScore", queryObj);

    const tempScores = [];
    if (result?.turns?.length) {
        maxScore.value = result.max ?? '';
        minScore.value = result.min ?? '';
        avgScore.value = result.avg ?? '';
        tempScores.push(...result.turns.map(scoreUtil.convertToUIScore));
    }

    playerScores.value = tempScores;
    calculateStats(result);
    //console.debug(JSON.stringify(playerScores.value));
};

const onClickPlayerRoundScore = (roundScore, index, playerIndex) => {
    showBigPinObj.value.visible = true;
    const rs = playerScores.value[playerIndex].roundScores[index];
    showBigPinObj.value.pins1 = rs.pins1;
    showBigPinObj.value.pins2 = rs.pins2;
    showBigPinObj.value.pins3 = rs.pins3;
    emit('round-click', roundScore, index, playerIndex);
};

const backToPreviousPage = () => {
    router.back();
};
</script>

<style>
.score-container {
    position: relative;
    background-color: #f5f7fa;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

/* 筛选弹窗样式 */
.filter-popup {
    height: 60vh;
    padding: 16px;
}

.popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
}

.popup-title {
    font-size: 16px;
    font-weight: 500;
}

.filter-button {
    display: flex;
    align-items: center;
    gap: 4px;
}

.fixed-header {
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    margin-bottom: 0;
}

.filter-section {
    padding: 8px;
    background-color: white;
    border-radius: 0 0 0.427rem 0.427rem;
    margin-bottom: 0;
}

/* 选择器组件样式 */
.selector-component {
    margin-bottom: 0.32rem;
    background-color: #f5f7fa;
    border-radius: 0.213rem;
    padding: 0.213rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* 统计卡片样式 */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    margin-bottom: 16px;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px;
    border-radius: 8px;
    background-color: #f8f9fa;
}

.stat-item.compact {
    padding: 6px;
}

.stat-item.compact span:first-child {
    font-size: 12px;
    margin-bottom: 4px;
}

.stat-item.compact span:last-child {
    font-size: 16px;
}

/* 分数相关样式 */
.high-score {
    color: #27ae60;
}

.low-score {
    color: #e74c3c;
}

.avg-score {
    color: #f39c12;
}

/* 分数包装器 */
.score-wrapper {
    position: relative;
    left: 50%;
    transform: translateX(-50%) scale(0.9);
    transform-origin: center;
    width: 111.11%;
}

/* 球员分数组件 */
.player-score-component {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 0.213rem;
    margin: 0 auto;
}

/* 弹出层样式 */
.pin-popup {
    width: 90%;
    max-width: 360px;
    border-radius: 0.427rem;
}

.pin-state {
    background-color: #f9f9f9;
    border-radius: 0.213rem;
    padding: 0.213rem;
}
</style>